
import Vue from 'vue';
import Datepicker from 'vuejs-datepicker';
import VueTimepicker from 'vue2-timepicker';
import 'vue2-timepicker/dist/VueTimepicker.css';

import Button from '../Button/Button.vue';
/**
- This datepicker is based on library vuejs-datepicker, you can pass any its exact props to this component
- The timepicker is based on library vue2-timepicker, pass its props inside props <b>timeProps</b>
**/
export default Vue.extend<any, any, any, any>({
  name: 'DateTimePicker',
  components: {
    Datepicker,
    TimePicker: VueTimepicker,
    QButton: Button
  },
  props: {
    /**
     * Set value on datepicker
     */
    value: {
      type: [String, Date],
      default: ''
    },
    /**
     * Set id on datepicker
     */
    id: {
      type: String,
      default: ''
    },
    /**
     * Set placeholder
     */
    placeholder: {
      type: String,
      default: 'Input date here'
    },
    /**
     * Set input on error state
     */
    error: {
      type: Boolean,
      default: false
    },
    /**
     * Set input on success state
     */
    success: {
      type: Boolean,
      default: false
    },
    /**
     * Set input on disabled state
     */
    disabled: {
      type: Boolean,
      default: false
    },
    /**
     * Set disabled dates {from: , to: }
     */
    disabledDates: {
      type: Object,
      default: () => {}
    },
    /**
     * Date Formatter
     */
    dateFormatter: {
      type: Function
    },
    /**
     * Set date format
     */
    dateFormat: {
      type: String,
      default: 'dd MMM yyyy'
    },
    /**
     * Set title label
     */
    titleLabel: {
      type: String,
      default: 'Select Date'
    },
    /**
     * Set cancel label
     */
    cancelLabel: {
      type: String,
      default: 'Cancel'
    },
    /**
     * Set enter label
     */
    enterLabel: {
      type: String,
      default: 'Enter'
    },
    /**
     * Set default date inside the date picker
     * This applies inside date picker modal
     */
    setDefaultDate: [String, Date],
    /**
     * Set the props to timepicker library
     */
    timeProps: Object
  },
  data() {
    return {
      date: new Date(),
      time: {
        HH: '00',
        mm: '00',
        ss: '00'
      },
      submittedDate: '',
      submittedTime: {},
      active: false,
      timeFormat: 'HH:mm',
      monthNames: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
      dayNames: ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']
    };
  },
  watch: {
    value() {
      this.setDateTimeValue();
    }
  },
  created() {
    this.setDateTimeValue();
  },
  computed: {
    containerClasses() {
      return {
        error: this.error,
        success: this.success,
        disabled: this.disabled
      };
    },
    submittedLabel() {
      // Label that shows inside the input field after we enter the datetime
      if (typeof this.dateFormatter === 'function') {
        return this.dateFormatter(this.date);
      }
      const date = new Date(this.submittedDate);
      // Get the date from submittedDate and time from submittedTime
      return this.setDateTimeLabel(date, this.submittedTime);
    },
    selectedLabel() {
      // Label that shows inside the datetime picker modal
      if (typeof this.dateFormatter === 'function') {
        const dateTimeValue = new Date(this.date);
        dateTimeValue.setHours(Number(this.time.HH), Number(this.time.mm || '00'), Number(this.time.ss || '00'));
        return this.dateFormatter(dateTimeValue);
      }
      return this.setDateTimeLabel(this.date, this.time);
    },
    focusDateValue() {
      if (this.value === '' && this.disabledDates && this.disabledDates.from) {
        return this.disabledDates.from;
      }
      if (typeof this.value === 'string' && this.value !== '') {
        return new Date(this.value);
      }
      return this.value ? this.value : undefined;
    },
    getDateTimeValue() {
      const dateTimeValue = new Date(this.date);
      dateTimeValue.setHours(Number(this.time.HH), Number(this.time.mm || '00'), Number(this.time.ss || '00'));
      return dateTimeValue;
    }
  },
  methods: {
    setDateTimeLabel(date: Date, time: any) {
      const minuteValue = `:${time.mm}`;
      const secondValue = `:${time.ss}`;

      let label = `${this.dayNames[date.getDay()]}, ${date.getDate()} ${
        this.monthNames[date.getMonth()]
      } ${date.getFullYear()} ${time.HH}`;
      if (time.mm) label += minuteValue;
      if (time.ss) label += secondValue;
      return label;
    },
    setDateTimeValue() {
      // Check if there is time format and move the value in new state
      if (this.timeProps && this.timeProps.format) {
        this.timeFormat = this.timeProps.format;
      }
      // Set the date to the filled default date in datepicker
      if (this.setDefaultDate) {
        this.date = this.setDefaultDate;
      } else if (this.value) {
        // Set the date
        this.date = this.value;
        this.submittedDate = this.value;
        // Set the time from date
        const getHours = this.getSpecificTime(this.date.getHours());
        const getMinutes = this.getSpecificTime(this.date.getMinutes());
        const getSeconds = this.getSpecificTime(this.date.getSeconds());
        this.time = {
          HH: getHours,
          mm: getMinutes,
          ss: getSeconds
        };
        this.submittedTime = this.time;
      } else {
        this.date = new Date();
        this.time = {
          HH: '00',
          mm: '00',
          ss: '00'
        };
      }
    },
    getSpecificTime(timeValue: number) {
      // If the time starts with 0, make sure the 0 stays
      return (timeValue < 10 ? '0' : '') + timeValue;
    },
    handleInput(selectedDate: Date) {
      this.date = selectedDate;
      /**
       * Method to input date value
       * @event input-date
       * @type {function}
       */
      this.$emit('input-date', this.date);
    },
    handleInputTime(selectedTime: string) {
      this.time = selectedTime;
      /**
       * Method to input time value
       * @event input-time
       * @type {function}
       */
      this.$emit('input-time', this.time);
    },
    handleEnter() {
      this.submittedDate = this.date;
      this.submittedTime = this.time;
      this.closeModal();
      /**
       * Method to input datetime value
       * @event input
       * @type {function}
       */
      this.$emit('input', this.getDateTimeValue);
    },
    closeModal() {
      this.active = false;
    },
    openModal(e: Event) {
      if (!this.disabled) {
        this.active = true;
      }
      /**
       * Method to open date input container
       * @event click
       * @type {function}
       */
      this.$emit('click', e);
    }
  }
});
